import { z } from "zod";

const Operator = z.enum(["GT", "GE", "LT", "LE", "EQ", "NE"]);
export type Operator = z.infer<typeof Operator>;

const CompositionOperator = z.enum(["MIN", "MAX", "EXACT", "ANY", "ALL"]);
export type CompositionOperator = z.infer<typeof CompositionOperator>;

export const Composition = z.object({
  operator: CompositionOperator,
  // A peer client (discovery_app, KMP/Swift) can serialise an absent count as
  // the JSON *string* "null" instead of real null. Coerce it back so the row
  // still validates; without this the whole condition fails to parse and
  // parseDesign would drop it -> deleted on the next autosave (2026-07-17
  // Expertise/Industry data-loss incident). On save this side re-emits proper
  // JSON null, healing the row end-to-end.
  value: z.preprocess((v) => (v === "null" ? null : v), z.number().int().nullable().optional()),
});
export type Composition = z.infer<typeof Composition>;

const AiMode = z.enum(["NO_AI", "CONTEXT_AI", "FULL_AI"]);
export type AiMode = z.infer<typeof AiMode>;

// On read, coerce a missing/null aiMode to NO_AI. Genesis records permit
// `aiMode = null` (the Java record has no @NonNull) and at least one JVM-side
// caller – MatchServiceDesignTest – writes that today; legacy data persisted
// before the global "AI für Fachwissen & Berufserfahrung" toggle convention
// can also carry it. Without this coercion the discriminated-union variant
// would fail to parse and parseDesign() would silently drop the whole row.
// NO_AI is the conservative default – it disables per-candidate model-gateway
// calls during solve, which is what a user looking at an unset toggle expects.
const AiModeOnRead = z.preprocess((v) => v == null ? "NO_AI" : v, AiMode);

export const LanguageLevel = z.enum(["A1", "A2", "B1", "B2", "C1", "C2", "NATIVE"]);
export type LanguageLevel = z.infer<typeof LanguageLevel>;

export const RequirementMode = z.enum(["COPILOT_ONLY", "HYBRID", "MANUAL"]);

// Match-Modus (Optimieren/Bewerten/Ranken). Immutabel ab Erstellung (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@1) — der
// Erstellungs-Picker setzt ihn. Lebt auf der Match.matchMode-Spalte (genesis #159 / V0035),
// nicht mehr im Design-JSONB: dieses Enum ist nur noch der geteilte UI-Typ (MatchData.matchMode).
export const MatchMode = z.enum(["OPTIMIZE", "ASSESS", "RANK"]);
export type MatchMode = z.infer<typeof MatchMode>;
export type RequirementMode = z.infer<typeof RequirementMode>;

// Muss/Kann fuer die Bewerbungs-Bewertung (genesis CriterionRequirement).
// Nur der Bewerbung-Scorer konsumiert sie; Solver und "Bewerten" ignorieren sie. Auf Read wird
// fehlend/null auf den Default gemappt (OPTIONAL), analog AiModeOnRead und passend zur
// null-Interpretation des genesis-Scorers.
export const CriterionRequirement = z.enum(["MUST", "OPTIONAL"]);
export type CriterionRequirement = z.infer<typeof CriterionRequirement>;
const CriterionRequirementOnRead = z.preprocess((v) => (v == null ? "OPTIONAL" : v), CriterionRequirement);

const Base = {
  operator: Operator,
  composition: Composition,
  active: z.boolean(),
  requirement: CriterionRequirementOnRead,
} as const;

// Permissive on parse – an empty id can briefly exist client-side while a
// row is being added; the codec must not silently drop the row. Producers
// (UI editors) guarantee a real UUID at save time.
const Uuid = z.string();

export const MatchConditionTypename = z.enum([
  "AgeCondition",
  "AnnualSalaryCondition",
  "CertificateCondition",
  "ChildrenCountCondition",
  "CitizenshipCondition",
  "CityCondition",
  "CountryCondition",
  "DriversLicenseCondition",
  "EducationCondition",
  "ExperienceCondition",
  "ExpertiseCondition",
  "GenderCondition",
  "IndustryExperienceCondition",
  "JobDepartmentCondition",
  "JobLocationCondition",
  "LanguageCondition",
  "MaritalCondition",
  "NationalityCondition",
]);
export type MatchConditionTypename = z.infer<typeof MatchConditionTypename>;


/** Integer condition value, rounded on read for the same reason as HealedInt below: a fractional value
 *  failed `.int()`, so the whole condition fell into `unknownConditions` and its row disappeared from
 *  the UI — invisible and unremovable, while the solver still applied it. Rounding recovers those rows;
 *  NumberValueInput rounds so no new ones appear. */
const HealedIntValue = z.preprocess(
  (v) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : v),
  z.number().int(),
);
export const MatchCondition = z.discriminatedUnion("__typename", [
  z.object({ __typename: z.literal("AgeCondition"), value: HealedIntValue, ...Base }).passthrough(),
  z.object({ __typename: z.literal("AnnualSalaryCondition"), value: HealedIntValue, ...Base }).passthrough(),
  z.object({ __typename: z.literal("CertificateCondition"), value: z.string(), aiMode: AiModeOnRead, ...Base }).passthrough(),
  z.object({ __typename: z.literal("ChildrenCountCondition"), value: HealedIntValue, ...Base }).passthrough(),
  z.object({ __typename: z.literal("CitizenshipCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({
    __typename: z.literal("CityCondition"),
    value: z.string(),
    aiMode: AiModeOnRead,
    radiusKm: z.number().int().min(1).max(1000).nullable().optional(),
    ...Base,
  }).passthrough(),
  z.object({ __typename: z.literal("CountryCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({ __typename: z.literal("DriversLicenseCondition"), value: z.string(), ...Base }).passthrough(),
  z.object({ __typename: z.literal("EducationCondition"), value: z.string(), aiMode: AiModeOnRead, ...Base }).passthrough(),
  z.object({ __typename: z.literal("ExperienceCondition"), value: z.string(), aiMode: AiModeOnRead, ...Base }).passthrough(),
  z.object({
    __typename: z.literal("ExpertiseCondition"),
    expertise: z.string(),
    // Genesis emits level: 0 as "unset" per RequirementsPrompts.java; the
    // strict min(1) was silently dropping such rows on parse. The genesis
    // solver currently ignores level anyway (MatchResultGenerateController
    // doesn't consult condition.level()), so 0 has no functional effect at
    // match time – but the contract should be aligned regardless.
    level: z.number().int().min(0).max(100),
    aiMode: AiModeOnRead,
    ...Base,
  }).passthrough(),
  z.object({ __typename: z.literal("GenderCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({ __typename: z.literal("IndustryExperienceCondition"), industryId: Uuid, minimumYears: z.number(), ...Base }).passthrough(),
  z.object({ __typename: z.literal("JobDepartmentCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({ __typename: z.literal("JobLocationCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({
    __typename: z.literal("LanguageCondition"),
    languageTag: z.string(),
    level: LanguageLevel,
    ...Base,
  }).passthrough(),
  z.object({ __typename: z.literal("MaritalCondition"), id: Uuid, ...Base }).passthrough(),
  z.object({ __typename: z.literal("NationalityCondition"), value: z.string(), ...Base }).passthrough(),
]);
export type MatchCondition = z.infer<typeof MatchCondition>;

export type ConditionByTypename<T extends MatchConditionTypename> = Extract<MatchCondition, { __typename: T }>;

// Matches genesis `TeamProfile { id, name, elementWeights: Map<CreativityElement, Integer> }`.
// Keys of `elementWeights` are full enum strings (e.g. "PERSEVERANCE"), not
// the 3-letter UI abbreviations; the UI layer translates for display.
export const TeamProfile = z
  .object({
    id: Uuid,
    name: z.string(),
    "elementWeights": z.record(z.string(), z.number().int()),
  })
  .passthrough();
export type TeamProfile = z.infer<typeof TeamProfile>;

// Wire types. Cross-platform canonical term is "Project" (genesis MR !236 /
// @adr SPARK-0021-project-umbrella-with-projectdocument-cross-repo@1) — the wire/JSON key is now `project` end-to-end.
// Matches genesis `ProjectDocument { backdropFileId, name, size?, mimeType? }`.
// ProjectDocument lifecycle is managed via the attach/detach mutations (genesis
// MR !236), but UpdateMatch still round-trips `documents` because genesis writes
// the JSONB design wholesale — omitting the field would delete every attached
// document on the next text edit (see match-design-mapping.ts buildProjectWire).
export const ProjectDocument = z
  .object({
    "backdropFileId": z.string(),
    name: z.string(),
    size: z.number().int().nullable().optional(),
    "mimeType": z.string().nullable().optional(),
  })
  .passthrough();
export type ProjectDocument = z.infer<typeof ProjectDocument>;

// Matches genesis `Project { name, shortDescription, description, documents }`.
// All fields nullable; the snapshot can be partially populated (e.g. user typed
// a name but no description yet).
export const Project = z
  .object({
    name: z.string().nullable().optional(),
    "shortDescription": z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    documents: z.array(ProjectDocument).nullable().optional(),
  })
  .passthrough();
export type Project = z.infer<typeof Project>;

export interface MatchDesign {
  teamCount: number | null;
  teamSize: number | null;
  teamProfile: TeamProfile | null;
  conditions: MatchCondition[];
  // Conditions spark's typed schema can't parse (a genesis variant added on
  // the server before spark models it, or a malformed value from another
  // client) are kept here verbatim and re-emitted by serializeDesign, never
  // dropped. The editors only ever read/write typed `conditions`, so an
  // unknown row is ignored for display/editing but never lost from storage.
  // Optional on the type only so existing MatchDesign literals stay valid;
  // parseDesign always populates it (empty when everything parsed).
  unknownConditions?: unknown[];
  aiCopilotText: string | null;
  candidateAssessmentPrompt?: string | null;
  candidateAssessmentDetail?: "BULLETS" | "SENTENCES" | "LONG_TEXT" | null;
  candidateAssessmentStrictness?: "NORMAL" | "STRICT" | "EXTREME" | null;
  candidateAssessmentOutcomeFocus?: "OPPORTUNITIES" | "BALANCED" | "RISKS" | null;
  // Stufenprüfung: wie streng die Mindeststufe von Fachwissen-Anforderungen bei FULL_AI
  // geprüft wird. null = LENIENT (Gnadenregel, genesis-Default).
  requirementLeniency?: "LENIENT" | "MUST_STRICT" | "STRICT" | null;
  requirementMode: RequirementMode | null;
  agenticAiSupported: boolean | null;
  /**
   * The AI agents this project is equipped with — project CONTEXT, never team members (@adr SPARK-0034-ai-agents-are-project-context-not-team-members@1).
   * The optimiser never sees them; they shape the requirement discussion and are shown with the
   * result. Stores a snapshot rather than bare ids so the entry stays legible to genesis and to the
   * assistant without the catalogue; display detail is resolved from `agent-catalogue` by `id`.
   */
  agents: MatchAgent[];
  project: Project | null;
}

/** What a match stores per selected agent: a snapshot, not a pointer. Small and self-describing. */
export interface MatchAgent {
  id: string;
  name: string;
  role: string;
  covers: string[];
}

export const MATCH_DESIGN_VERSION = 2;

export const EMPTY_DESIGN: MatchDesign = {
  teamCount: null,
  teamSize: null,
  teamProfile: null,
  conditions: [],
  unknownConditions: [],
  aiCopilotText: null,
  candidateAssessmentPrompt: null,
  candidateAssessmentDetail: null,
  candidateAssessmentStrictness: null,
  candidateAssessmentOutcomeFocus: null,
  requirementLeniency: null,
  requirementMode: null,
  agenticAiSupported: null,
  agents: [],
  project: null,
};

// A count that a peer client (or spark, before the producer was bounded) may have written as a
// non-integer. `parseDesign` uses the THROWING RawDesign.parse and runs on every render of the match
// editor, so a fractional teamSize made that match permanently unopenable — the value could not even be
// corrected, because the editor would not render. Rounding on READ heals those matches; the producer is
// bounded separately so new ones cannot be created.
const HealedInt = z.preprocess(
  (v) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : v),
  z.number().int().nullable().optional(),
);

// Tolerant on read: a malformed entry is dropped rather than making the match unopenable, the same
// stance parseDesign takes for conditions. Demo data and hand-edited blobs both land here.
const MatchAgentRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string().optional().default(""),
  covers: z.array(z.string()).optional().default([]),
});

const RawDesign = z.object({
  "teamCount": HealedInt,
  "teamSize": HealedInt,
  "teamProfile": TeamProfile.nullable().optional(),
  "conditions": z.array(z.unknown()).optional(),
  "aiCopilotText": z.string().nullable().optional(),
  "candidateAssessmentPrompt": z.string().nullable().optional(),
  "candidateAssessmentDetail": z.enum(["BULLETS", "SENTENCES", "LONG_TEXT"]).nullable().optional(),
  "candidateAssessmentStrictness": z.enum(["NORMAL", "STRICT", "EXTREME"]).nullable().optional(),
  "candidateAssessmentOutcomeFocus": z.enum(["OPPORTUNITIES", "BALANCED", "RISKS"]).nullable().optional(),
  "requirementLeniency": z.enum(["LENIENT", "MUST_STRICT", "STRICT"]).nullable().optional(),
  "requirementMode": RequirementMode.nullable().optional(),
  "agenticAiSupported": z.boolean().nullable().optional(),
  "agents": z.array(MatchAgentRefSchema).nullable().optional(),
  "project": Project.nullable().optional(),
}).passthrough();

export function parseDesign(input: unknown): MatchDesign {
  if (input == null || typeof input !== "object" || Array.isArray(input)) {
    return { ...EMPTY_DESIGN };
  }
  const raw = RawDesign.parse(input);

  // Preserve, don't drop. A condition spark can't type is round-tripped
  // verbatim by serializeDesign instead of being deleted on the next
  // autosave. See the `unknownConditions` field docs + the 2026-07-17
  // incident where a peer client wrote composition.value as the string
  // "null" and every Expertise/Industry criterion vanished on save.
  const conditions: MatchCondition[] = [];
  const unknownConditions: unknown[] = [];
  for (const c of raw.conditions ?? []) {
    const parsed = MatchCondition.safeParse(c);
    if (parsed.success) {
      conditions.push(parsed.data);
      continue;
    }
    /* v8 ignore next -- unreachable in any environment spark runs in (browser, jsdom): the guard exists
       only so a console-less host cannot turn a preserved condition into a crash */
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        "[design-codec] preserving unrecognised MatchCondition (round-tripped, not editable)",
        c,
        parsed.error.issues,
      );
    }
    unknownConditions.push(c);
  }

  return {
    teamCount: raw["teamCount"] ?? null,
    teamSize: raw["teamSize"] ?? null,
    teamProfile: raw["teamProfile"] ?? null,
    conditions,
    unknownConditions,
    aiCopilotText: raw["aiCopilotText"] ?? null,
    candidateAssessmentPrompt: raw["candidateAssessmentPrompt"] ?? null,
    candidateAssessmentDetail: raw["candidateAssessmentDetail"] ?? null,
    candidateAssessmentStrictness: raw["candidateAssessmentStrictness"] ?? null,
    candidateAssessmentOutcomeFocus: raw["candidateAssessmentOutcomeFocus"] ?? null,
    requirementLeniency: raw["requirementLeniency"] ?? null,
    requirementMode: raw["requirementMode"] ?? null,
    agenticAiSupported: raw["agenticAiSupported"] ?? null,
    agents: raw["agents"] ?? [],
    project: raw["project"] ?? null,
  };
}

// Emits only genesis-known design keys. Spark-private UI state must either get
// a typed home in MatchDesign or stay React-only; otherwise another client
// cannot preserve or understand it even though genesis tolerates unknown
// legacy keys on read.
export function serializeDesign(design: MatchDesign): Record<string, unknown> {
  const out: Record<string, unknown> = { version: MATCH_DESIGN_VERSION };
  if (design.teamCount != null) out["teamCount"] = design.teamCount;
  if (design.teamSize != null) out["teamSize"] = design.teamSize;
  if (design.teamProfile != null) out["teamProfile"] = design.teamProfile;
  // Re-append conditions spark couldn't type so they survive the round-trip
  // (see parseDesign / the MatchDesign.unknownConditions docs). They stay
  // inside the known `conditions` key, so genesis's strict top-level
  // deserialization is unaffected.
  const unknownConditions = design.unknownConditions ?? [];
  out["conditions"] =
    unknownConditions.length > 0 ? [...design.conditions, ...unknownConditions] : design.conditions;
  if (design.aiCopilotText != null) out["aiCopilotText"] = design.aiCopilotText;
  if (design.candidateAssessmentPrompt != null) {
    out["candidateAssessmentPrompt"] = design.candidateAssessmentPrompt;
  }
  if (design.candidateAssessmentDetail != null) {
    out["candidateAssessmentDetail"] = design.candidateAssessmentDetail;
  }
  if (design.candidateAssessmentStrictness != null) {
    out["candidateAssessmentStrictness"] = design.candidateAssessmentStrictness;
  }
  if (design.candidateAssessmentOutcomeFocus != null) {
    out["candidateAssessmentOutcomeFocus"] = design.candidateAssessmentOutcomeFocus;
  }
  if (design.requirementLeniency != null) {
    out["requirementLeniency"] = design.requirementLeniency;
  }
  if (design.requirementMode != null) out["requirementMode"] = design.requirementMode;
  if (design.agenticAiSupported != null) out["agenticAiSupported"] = design.agenticAiSupported;
  // Always emitted once non-empty; genesis does not model the key but preserves it (genesis#264),
  // and Match.design resolves from the raw map, so the assistant reads it too.
  if (design.agents.length > 0) out["agents"] = design.agents;
  // matchMode is NOT emitted: @adr SPARK-0029-match-three-case-model-optimize-assess-rank@1 §2 moved it to the immutable Match.matchMode
  // column (genesis #159 / V0035). genesis strips it from the blob anyway
  // (withoutMatchMode on create + update), so persisting it here would only re-introduce
  // the drift-prone dual representation the ADR removed.
  if (design.project != null) out["project"] = design.project;
  return out;
}

export function newCondition<T extends MatchConditionTypename>(
  typename: T,
  partial: Omit<ConditionByTypename<T>, "__typename" | keyof typeof Base | "active"> & {
    operator?: Operator;
    composition?: Composition;
    active?: boolean;
    requirement?: CriterionRequirement;
  },
): ConditionByTypename<T> {
  return {
    ...partial,
    __typename: typename,
    // Diese vier Felder MUESSEN nach dem Spread stehen: partial's Object-Spread kopiert
    // eine Property, sobald ihr Key existiert, unabhaengig davon ob ihr Wert undefined
    // (oder null) ist - anders als `??`, das genau dafuer den Fallback nehmen wuerde.
    // Vor 2026-09-01 stand `...partial` als letztes und ein explizit uebergebenes
    // `composition: undefined` (ExpertiseSection, Rank-Fall) hat den bereits berechneten
    // Default wieder verworfen.
    operator: partial.operator ?? "EQ",
    composition: partial.composition ?? { operator: "ALL", value: null },
    active: partial.active ?? true,
    requirement: partial.requirement ?? "OPTIONAL",
  } as ConditionByTypename<T>;
}
